import type { Masjid } from './types';
import { dayTimes, type TimetableIndex } from './prayer';
import { zonedToInstant, ymd } from './time';

/**
 * Makruh times: when no salah should be prayed. Two each day, from the masjid's timetable:
 *  - after sunrise, from sunrise until the sun has fully risen (15 minutes by default, 10 to 20);
 *  - zawal, when the sun is at its height, the minutes just before Dhuhr begins (20 by default, 5 to 30).
 * During either, the Makruh Times widget shows a solid red banner ("Makruh time · please do not pray any salah until
 * …") over the screen's news ticker, or in its own place along the bottom when there's no ticker.
 */
export type MakruhProps = { afterSunrise: number; beforeDhuhr: number; place: 'ticker' | 'own' };
export const MAKRUH_DEFAULTS: MakruhProps = { afterSunrise: 15, beforeDhuhr: 20, place: 'ticker' };
export const MAKRUH_LIMITS = { afterSunrise: [10, 20], beforeDhuhr: [5, 30] } as const;
/** The banner's colours: an alert, so they can't be changed. */
export const MAKRUH_RED = '#D70015', MAKRUH_INK = '#FFFFFF';

export type MakruhPeriod = { kind: 'sunrise' | 'zawal'; from: Date; until: Date };

const clamp = (v: unknown, [lo, hi]: readonly [number, number], d: number) => { const n = Math.round(Number(v)); return Number.isFinite(n) ? Math.max(lo, Math.min(hi, n)) : d; };

/** The day's makruh periods (none when the timetable has no sunrise or Dhuhr for it). */
export function makruhPeriods(m: Masjid, tt: TimetableIndex, day: string, p: Partial<MakruhProps> = {}): MakruhPeriod[] {
  const t = dayTimes(m, tt, day);
  const out: MakruhPeriod[] = [];
  const after = clamp(p.afterSunrise, MAKRUH_LIMITS.afterSunrise, MAKRUH_DEFAULTS.afterSunrise);
  const before = clamp(p.beforeDhuhr, MAKRUH_LIMITS.beforeDhuhr, MAKRUH_DEFAULTS.beforeDhuhr);
  if (t.start.sunrise) { const s = zonedToInstant(day, t.start.sunrise, m.timezone); out.push({ kind: 'sunrise', from: s, until: new Date(s.getTime() + after * 60_000) }); }
  if (t.start.dhuhr) { const d = zonedToInstant(day, t.start.dhuhr, m.timezone); out.push({ kind: 'zawal', from: new Date(d.getTime() - before * 60_000), until: d }); }
  return out;
}

/** The makruh period happening at `at`, or null. */
export function makruhAt(m: Masjid, tt: TimetableIndex, at: Date, p: Partial<MakruhProps> = {}): MakruhPeriod | null {
  return makruhPeriods(m, tt, ymd(at, m.timezone), p).find((x) => at >= x.from && at < x.until) ?? null;
}

/** The next makruh period from `at` (today's or tomorrow's), for previews and the Studio. */
export function nextMakruh(m: Masjid, tt: TimetableIndex, at: Date, p: Partial<MakruhProps> = {}): MakruhPeriod | null {
  for (const day of [ymd(at, m.timezone), ymd(new Date(at.getTime() + 86_400_000), m.timezone)]) {
    const hit = makruhPeriods(m, tt, day, p).find((x) => at < x.until);
    if (hit) return hit;
  }
  return null;
}
